import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box, Button, Chip, Divider, IconButton, Paper, Stack, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import IELTSResults from './IELTSResults';
import { useStoredUserData } from '../authentication/userSession';

const SidebarCard = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  top: '120px',
  left: '24px',
  width: '220px',
  borderRadius: '18px',
  padding: '12px',
  border: '1px solid rgba(153, 108, 254, 0.14)',
  boxShadow: '0 14px 30px rgba(41, 22, 89, 0.07)',
  zIndex: 10,
  [theme.breakpoints.down('md')]: {
    position: 'static',
    width: 'auto',
    margin: '96px 16px 0',
  },
}));

const NavButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ active }) => ({
  justifyContent: 'flex-start',
  width: '100%',
  borderRadius: '12px',
  padding: '9px 11px',
  marginBottom: '6px',
  textTransform: 'none',
  fontSize: '0.9rem',
  fontWeight: 800,
  color: active ? '#fff' : '#3a2f55',
  background: active ? 'linear-gradient(135deg, #996CFE 0%, #7351e9 100%)' : 'transparent',
  boxShadow: active ? '0 8px 18px rgba(153, 108, 254, 0.24)' : 'none',
  '&:hover': {
    background: active ? 'linear-gradient(135deg, #996CFE 0%, #7351e9 100%)' : '#f5f0ff',
  },
}));

const ResultsContentShell = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hideOverview',
})(({ hideOverview, theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #f8fbff 0%, #f7f1ff 48%, #fffaf2 100%)',
  '& > .MuiContainer-root': {
    marginLeft: '268px',
    marginRight: '24px',
    maxWidth: 'none',
    width: 'auto',
  },
  ...(hideOverview ? {
    '& > .MuiContainer-root': {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: '268px',
      marginRight: '24px',
      maxWidth: 'none',
      width: 'auto',
    },
    '& > .MuiContainer-root > h1.MuiTypography-root': {
      display: 'none',
    },
    '& > .MuiContainer-root > .MuiCard-root:first-of-type': {
      display: 'none',
    },
    '& > .MuiContainer-root > .MuiCard-root': {
      marginBottom: '14px',
      overflow: 'hidden',
    },
    '& > .MuiContainer-root > .MuiCard-root .MuiCardContent-root': {
      padding: '18px 20px',
    },
    '& > .MuiContainer-root > .MuiCard-root .MuiCardContent-root:last-child': {
      paddingBottom: '18px',
    },
    '& > .MuiContainer-root > .MuiCard-root h6': {
      fontSize: '1rem',
      marginBottom: '10px',
    },
    '& > .MuiContainer-root > .MuiCard-root .MuiGrid-container': {
      marginTop: '-6px',
      marginLeft: '-6px',
      width: 'calc(100% + 6px)',
    },
    '& > .MuiContainer-root > .MuiCard-root .MuiGrid-item': {
      paddingTop: '6px',
      paddingLeft: '6px',
    },
    '& > .MuiContainer-root > .MuiCard-root .MuiGrid-item > .MuiBox-root': {
      padding: '13px',
      minHeight: 'auto',
    },
    '& > .MuiContainer-root > .MuiCard-root h3': {
      fontSize: '1.7rem',
      lineHeight: 1.1,
      marginBottom: '4px',
    },
    '& > .MuiContainer-root > .MuiCard-root .MuiBox-root h6': {
      fontSize: '0.88rem',
      marginBottom: '2px',
    },
    '& > .MuiContainer-root > .MuiCard-root .MuiBox-root .MuiTypography-body2': {
      fontSize: '0.74rem',
      lineHeight: 1.25,
    },
    '& > .MuiContainer-root > .MuiCard-root .MuiDivider-root': {
      marginTop: '12px',
      marginBottom: '12px',
    },
    '& > .MuiContainer-root > .MuiCard-root .MuiTypography-subtitle1': {
      fontSize: '0.86rem',
      marginBottom: '8px',
    },
    '& > .MuiContainer-root > .MuiDivider-root:last-of-type': {
      display: 'none',
    },
    '& > .MuiContainer-root > .MuiDivider-root:last-of-type + .MuiBox-root': {
      display: 'none',
    },
    '& > .MuiContainer-root button:has(.MuiButton-startIcon svg[data-testid="HomeIcon"])': {
      display: 'none',
    },
  } : {}),
  [theme.breakpoints.down('md')]: {
    '& > .MuiContainer-root': {
      marginLeft: 0,
      marginRight: 0,
      paddingTop: '20px',
    },
  },
}));

const DASHBOARD_OFFER_DURATION_MS = 7 * 24 * 60 * 60 * 1000;
const DASHBOARD_OFFER_URGENCY_DAYS = 3;
const DASHBOARD_OFFER_URGENCY_HOURS = 24;

function getDashboardOfferStorageKey() {
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const userId = userData?.UserId || 'guest';
  return `ielts-dashboard-offer-start-at-${userId}`;
}

function getDashboardOfferBarStorageKey() {
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const userId = userData?.UserId || 'guest';
  return `ielts-dashboard-offer-bar-visible-${userId}`;
}

function formatDashboardOfferTime(milliseconds) {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  return `${hours}h ${minutes}m`;
}

const IELTSResultsWithSidebar = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isGroupDetailView = Boolean(searchParams.get('groupId'));
  const [offerCodeCopied, setOfferCodeCopied] = useState(false);
  const [showOfferBar, setShowOfferBar] = useState(localStorage.getItem(getDashboardOfferBarStorageKey()) === '1');
  const [nowTs, setNowTs] = useState(() => Date.now());
  const userData = useStoredUserData() || {};
  const isSubscribed = userData?.IsSubscribed || false;
  const canSeeOffer = Number(userData?.PortalID) === 1 && !isSubscribed;
  const offerStartTs = useMemo(() => {
    const storedStart = Number(localStorage.getItem(getDashboardOfferStorageKey()));
    return Number.isFinite(storedStart) && storedStart > 0 ? storedStart : null;
  }, []);

  React.useEffect(() => {
    const timerId = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(timerId);
  }, []);

  const offerRemainingMs = useMemo(() => {
    if (!offerStartTs) return 0;
    return offerStartTs + DASHBOARD_OFFER_DURATION_MS - nowTs;
  }, [offerStartTs, nowTs]);
  const isOfferExpired = offerRemainingMs <= 0;
  const isOfferFinalHours = offerRemainingMs > 0 && offerRemainingMs <= DASHBOARD_OFFER_URGENCY_HOURS * 60 * 60 * 1000;
  const isOfferFinalDays = offerRemainingMs > 0 && offerRemainingMs <= DASHBOARD_OFFER_URGENCY_DAYS * 24 * 60 * 60 * 1000;
  const offerUrgencyLabel = isOfferFinalHours ? 'Final hours' : isOfferFinalDays ? 'Ending soon' : 'Limited offer';

  const handleCopyOfferCode = async () => {
    if (isOfferExpired) return;
    try {
      await navigator.clipboard.writeText('learnswift20');
      setOfferCodeCopied(true);
      setTimeout(() => setOfferCodeCopied(false), 1800);
    } catch (error) {
      setOfferCodeCopied(false);
    }
  };

  const resultsOfferBar = canSeeOffer && !isOfferExpired && showOfferBar ? (
    <Box
      sx={{
        mb: 2,
        p: { xs: 1.3, sm: 1.6 },
        borderRadius: '16px',
        border: `1px solid ${isOfferFinalHours ? '#fb7185' : isOfferFinalDays ? '#f59e0b' : '#a78bfa'}`,
        background: isOfferFinalHours
          ? 'linear-gradient(135deg, #fff1f3 0%, #ffe4e8 42%, #ffe2e6 100%)'
          : isOfferFinalDays
            ? 'linear-gradient(135deg, #fff7ed 0%, #ffefdc 48%, #ffe8ce 100%)'
            : 'linear-gradient(135deg, #f6f1ff 0%, #eef5ff 52%, #e8fbff 100%)',
        boxShadow: isOfferFinalHours
          ? '0 14px 30px rgba(244, 63, 94, 0.18)'
          : '0 14px 30px rgba(124, 58, 237, 0.14)',
        display: 'flex',
        justifyContent: 'space-between',
        gap: 1.2,
        alignItems: { xs: 'flex-start', sm: 'center' },
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
        <AutoAwesomeIcon sx={{ color: isOfferFinalHours ? '#e11d48' : '#7c3aed', fontSize: 22 }} />
        <Box>
          <Typography sx={{ color: '#201238', fontWeight: 950, fontSize: { xs: 16, sm: 18 }, lineHeight: 1.1 }}>
            1 Free 1-on-1 Class
          </Typography>
          <Typography sx={{ color: '#5b3d9a', fontWeight: 900, fontSize: 12.8 }}>
            Plus 20% off with code learnswift20
          </Typography>
          <Typography sx={{ color: '#564b70', fontWeight: 700, fontSize: 12.2 }}>
            {offerUrgencyLabel}: {formatDashboardOfferTime(offerRemainingMs)} left
          </Typography>
        </Box>
      </Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ ml: 'auto' }}>
        <Chip
          label={offerCodeCopied ? 'Code copied' : 'learnswift20'}
          onClick={handleCopyOfferCode}
          sx={{
            fontWeight: 900,
            cursor: 'pointer',
            color: '#7a3d00',
            background: '#FFE0B2',
            border: '1px solid #FFB74D',
            '&:hover': { background: '#FFD28A' },
          }}
        />
        <Button
          onClick={() => navigate('/plan-info')}
          size="small"
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 900,
            borderRadius: '999px',
            background: 'linear-gradient(135deg, #996CFE 0%, #7351e9 100%)',
          }}
        >
          View plans
        </Button>
        <IconButton
          size="small"
          onClick={() => {
            setShowOfferBar(false);
            localStorage.removeItem(getDashboardOfferBarStorageKey());
          }}
          sx={{ color: '#6b5a8f', border: '1px solid rgba(91, 74, 122, 0.2)' }}
          title="Hide offer bar"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  ) : null;

  return (
    <Box>
      <SidebarCard>
        <NavButton active={false} onClick={() => navigate('/ielts-dashboard')}>
          Dashboard
        </NavButton>
        <NavButton active>
          Results
        </NavButton>
        <Divider sx={{ my: 1 }} />
        <NavButton active={false} onClick={() => navigate('/ielts-dashboard?view=sectional')}>
          Sectional Mock Test
        </NavButton>
        <NavButton active={false} onClick={() => navigate('/ielts-dashboard?view=full')}>
          Full Mock Test
        </NavButton>
        <NavButton active={false} onClick={() => navigate('/ielts-dashboard?view=community')}>
          Community
        </NavButton>
      </SidebarCard>
      <ResultsContentShell hideOverview={!isGroupDetailView}>
        <IELTSResults topContent={resultsOfferBar} />
      </ResultsContentShell>
    </Box>
  );
};

export default IELTSResultsWithSidebar;
